import { FlowEventHandlers } from '../../../shared/flow-event';
import {
  SignupByOtpOneCredentialFlowEventDefinition,
  SignupByOtpOneCredentialFlowEventState,
} from './types';
import { appTimingUtil, monitor } from '../../../platform/monitoring';
import { BusinessPerfMetric } from '../../../platform/monitoring/performance/enums';
import { PageName } from '@shopee-user/url';
import {
  generateTrackingHelpButtonClick,
  generateTrackingNextButtonClick,
  generateTrackingSignupView,
  generateTrackingSubmitFormButtonClick,
  TrackingDataFieldTypeSignupMethod,
  generateTrackingSignupAlreadyRegisteredView,
  generateTrackingAlreadyRegisteredLoginButtonClick,
  generateTrackingAlreadyRegisteredHelpButtonClick,
  generateTrackingAlreadyRegisteredContactUsButtonClick,
  TrackingDataFieldTypeAccountType,
  generateTrackingSignupSetPasswordView,
  generateTrackingSignupSetPasswordSignupButtonClick,
  generateTrackingSignupSetPasswordBackButtonClick,
  generateTrackingSignupSuccessView,
  generateTrackingSignupSuccessBackButtonClick,
  generateTrackingSignupSuccessAction,
  generateTrackingSignupSuccessNextButtonClick,
} from '@shopee-user/tracking-signup';
import { sendGTMEventSignupSuccess } from '@tob/shared/tracking/signup';
import {
  generateTrackingAccountBannedContactUsButtonClick,
  generateTrackingAccountBannedOkButtonClick,
  generateTrackingAccountBannedImpression,
  TrackingDataFieldTypeOtpScenario,
} from '@shopee-user/tracking-otp-enter-verification';
import {
  generateTrackingLoginSuccessAction,
  TrackingDataFieldTypeLoginMethod,
} from '@shopee-user/tracking-login';
import { OtpType } from '@shopee-user/otp-hook-base-constants';

const OTP_SCENARIO = TrackingDataFieldTypeOtpScenario.Signup;

export const TRACKER_NAME = 'signup-by-otp-one-credential-optional';

export const createInitialState = (): SignupByOtpOneCredentialFlowEventState => {
  return {
    authKey: '',
  };
};

export function createTracker() {
  const trackPageLoad = ({ pageName }: { pageName: string }) => {
    const softNavigationDuration = appTimingUtil.getDurationFromSoftNavigationStart();
    const isSoftNavigation = softNavigationDuration > 0;

    monitor.sendDuration({
      name: BusinessPerfMetric.PageLoad,
      duration: isSoftNavigation
        ? softNavigationDuration
        : appTimingUtil.getAppAliveDuration(),
      data: {
        soft_nav: isSoftNavigation ? 'true' : 'false',
        page_name: pageName,
        // some of custom metadatas are not allowed to be accessed in Grafana because of database performance concerns
        // so here we use status to indicate the navigation type
        status: isSoftNavigation ? 'soft_nav' : 'hard_nav',
      },
    });
  };

  const handlers: FlowEventHandlers<
    SignupByOtpOneCredentialFlowEventDefinition,
    SignupByOtpOneCredentialFlowEventState
  > = {
    onSignupRequireAuthKeyPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupView());
        trackPageLoad({
          pageName: PageName.PageSignupByOTP,
        });
      };
    },
    onSignupSubmitAuthKey: ({ phone }: { phone: string | undefined }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingNextButtonClick({
            method: phone
              ? TrackingDataFieldTypeSignupMethod.Phone
              : TrackingDataFieldTypeSignupMethod.Email,
          })
        );
      };
    },
    onSignupSignupHelpClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingHelpButtonClick());
      };
    },
    onSignupSubmitExternalSignupFormClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSubmitFormButtonClick());
      };
    },
    onSignupAccountBannedPageInit: () => {
      return ({ tools }) => {
        tools.sendImpressionEvent(
          generateTrackingAccountBannedImpression({ scenario: OTP_SCENARIO })
        );
      };
    },
    onSignupAccountBannedOkClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedOkButtonClick({ scenario: OTP_SCENARIO })
        );
      };
    },
    onSignupAccountBannedContactUsClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedContactUsButtonClick({
            scenario: OTP_SCENARIO,
          })
        );
      };
    },
    onSignupAlreadyRegisteredPageInit: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupAlreadyRegisteredView({ method })
        );
      };
    },
    onSignupAlreadyRegisteredLoginClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredLoginButtonClick({ method })
        );
      };
    },
    onSignupAlreadyRegisteredHelpClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredHelpButtonClick({ method })
        );
      };
    },
    onSignupAlreadyRegisteredContactUsClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredContactUsButtonClick({ method })
        );
      };
    },
    onSignupSetPasswordPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupSetPasswordView());
      };
    },
    onSignupSubmitPassword: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSetPasswordSignupButtonClick()
        );
      };
    },
    onSignupSetPasswordBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSetPasswordBackButtonClick()
        );
      };
    },
    onSignupSignupSuccessInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupSuccessView());
      };
    },
    onSignupSignupSuccessAction: ({
      signupMethod,
      otpType,
      userId,
    }: {
      signupMethod: TrackingDataFieldTypeSignupMethod;
      otpType: OtpType | undefined;
      userId: number;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessAction({
            accountType: TrackingDataFieldTypeAccountType.TobAccount,
            method: signupMethod,
            otpType,
          })
        );
        sendGTMEventSignupSuccess({
          userId,
          method: signupMethod,
          accountType: TrackingDataFieldTypeAccountType.OneCredential,
        });
      };
    },
    onSignupSignupSuccessBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessBackButtonClick()
        );
      };
    },
    onSignupSignupSuccessNextClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessNextButtonClick()
        );
      };
    },
    onSignupLoginSuccess: ({
      accountType,
      loginMethod,
    }: {
      accountType: TrackingDataFieldTypeAccountType;
      loginMethod: TrackingDataFieldTypeLoginMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSuccessAction({
            loginMethod,
            accountType,
          })
        );
      };
    },
  };

  return {
    name: TRACKER_NAME,
    handlers,
  };
}
